import { useContext } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import { Box, rem, Tabs, Tooltip } from "@mantine/core";

import {
  IconCurrencyDollar,
  IconDirectionSign,
  IconFiles,
  IconFirstAidKit,
  IconId,
  IconMicroscope,
  IconPill,
  IconStar,
  Icon as TablerIcon,
} from "@tabler/icons-react";

import { ScrollingTabsList } from "@/components/Tabs";
import { ProfileContext } from "@/contexts/profile";
import { useIsMobile } from "@/hooks/useBreakpoint";
import PermissionGate from "@/permission/components/PermissionGate";
import { LAB_RESULTS_VIEW_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";

import classes from "./ClientTabs.module.css";

/**
 * `lab-results-view`: disabled for staff without lab results view permissions.
 * `under-development`: staff only, disabled, with a "Feature under development" tooltip.
 */
type ClientTabAccess = "lab-results-view" | "under-development";

interface ClientTabDefinition {
  value: string;
  label: string;
  /** Defaults to `label`. */
  ariaLabel?: string;
  icon: TablerIcon;
  testId: string;
  access?: ClientTabAccess;
}

// On desktop the back row sits behind the front row like folder tabs. Phones get one
// scrolling row, front row first; the active tab is scrolled into view on every change.
const BACK_ROW_TABS: ClientTabDefinition[] = [
  {
    value: "profile",
    label: "Client Profile",
    icon: IconId,
    testId: "patient-tab-profile",
  },
  {
    value: "results",
    label: "Results",
    icon: IconMicroscope,
    testId: "patient-tab-results",
    access: "lab-results-view",
  },
  {
    value: "referrals",
    label: "Referrals",
    icon: IconDirectionSign,
    testId: "patient-tab-referrals",
    access: "under-development",
  },
  { value: "files", label: "Files", icon: IconFiles, testId: "patient-tab-files" },
  { value: "billing", label: "Billing", icon: IconCurrencyDollar, testId: "patient-tab-billing" },
];

const FRONT_ROW_TABS: ClientTabDefinition[] = [
  {
    value: "overview",
    label: "Overview",
    icon: IconStar,
    testId: "patient-tab-overview",
  },
  {
    value: "encounters",
    label: "Encounters",
    icon: IconFirstAidKit,
    testId: "patient-tab-encounters",
  },
  {
    value: "pam",
    label: "Problems, Allergies, & Meds",
    ariaLabel: "Problems, Allergies, & Medicine",
    icon: IconPill,
    testId: "patient-tab-pam",
  },
  {
    value: "history",
    label: "Client History",
    icon: IconPill,
    testId: "patient-tab-history",
  },
  {
    value: "assessments-vitals/assessments",
    label: "Assessments & Vitals",
    icon: IconFirstAidKit,
    testId: "patient-tab-assessments-vitals",
  },
];

function ClientTab({ tab }: { tab: ClientTabDefinition }) {
  const TabIcon = tab.icon;
  const tabElement = (
    <Tabs.Tab
      value={tab.value}
      data-testid={tab.testId}
      className={classes.clientTabs}
      aria-label={tab.ariaLabel ?? tab.label}
      disabled={tab.access === "under-development"}
      leftSection={<TabIcon style={{ width: rem(16), height: rem(16) }} />}
    >
      {tab.label}
    </Tabs.Tab>
  );

  switch (tab.access) {
    case "under-development":
      return <Tooltip label="Feature under development">{tabElement}</Tooltip>;
    case "lab-results-view":
      return (
        <PermissionGate mode="disable" requiredPermissions={LAB_RESULTS_VIEW_PERMISSION_FLAGS}>
          {tabElement}
        </PermissionGate>
      );
    default:
      return tabElement;
  }
}

export default function ClientTabs() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams() as { clientId: string };
  const activeTab = getActiveTab(location.pathname, clientId);
  const { isPatient } = useContext(ProfileContext);
  const isMobile = useIsMobile();

  const isVisible = (tab: ClientTabDefinition) =>
    !(isPatient && tab.access === "under-development");
  const backRowTabs = BACK_ROW_TABS.filter(isVisible);
  const frontRowTabs = FRONT_ROW_TABS.filter(isVisible);

  function onTabChange(newTabName: string | null): void {
    navigate(`/clients/${clientId}/${newTabName}`);
  }

  return (
    <Box w="100%" p={{ base: "xs", sm: 16 }}>
      <Tabs value={activeTab} variant="unstyled" classNames={classes} onChange={onTabChange}>
        {isMobile ? (
          <ScrollingTabsList
            className={classes.scrollingList}
            data-testid="patient-tabs-scrolling-list"
          >
            {[...frontRowTabs, ...backRowTabs].map((tab) => (
              <ClientTab tab={tab} key={tab.value} />
            ))}
          </ScrollingTabsList>
        ) : (
          <>
            <Tabs.List grow className={classes.backList}>
              {backRowTabs.map((tab) => (
                <ClientTab tab={tab} key={tab.value} />
              ))}
            </Tabs.List>
            <Tabs.List grow className={classes.frontList} data-testid="patient-tabs-front-list">
              {frontRowTabs.map((tab) => (
                <ClientTab tab={tab} key={tab.value} />
              ))}
            </Tabs.List>
          </>
        )}
        <Box bg="white" className={classes.outletBox}>
          <Outlet />
        </Box>
      </Tabs>
    </Box>
  );
}

// Note: route format must be /clients/:clientId/{tabName}/...
function getActiveTab(pathname: string, clientId: string): string {
  const activeTab = pathname.replace(`/clients/${clientId}/`, "").split("/")[0];
  if (activeTab === "assessments-vitals") {
    // TODO: Figure out a better way to handle this
    return "assessments-vitals/assessments";
  }
  return activeTab;
}
